/**
 *  @swagger
 *  definitions:
 *    RegattaCoursePointParams:
 *      type: object
 *      properties:
 *        RegattaCourseId:
 *          description: ID of the associated RegattaCourse
 *          type: string
 *        SortId:
 *          description: Sort ID of the RegattaCoursePoint
 *          type: integer
 *        Type:
 *          description: Type of the RegattaCoursePoint
 *          $ref: '#/components/enums/RegattaCoursePointType'
 *        Side:
 *          description: Side of the RegattaCoursePoint
 *          $ref: '#/components/enums/RegattaCoursePointSide'
 *        Name:
 *          description: Name of the RegattaCoursePoint
 *          type: string
 *        Color:
 *          description: Color of the RegattaCoursePoint
 *          type: string
 *        Lat:
 *          description: Lat of the RegattaCoursePoint
 *          type: integer
 *        Lng:
 *          description: Lng of the RegattaCoursePoint
 *          type: integer
 *        Name2:
 *          description: Name 2 of the RegattaCoursePoint
 *          type: string
 *        Color2:
 *          description: Color 2 of the RegattaCoursePoint
 *          type: string
 *        Lat2:
 *          description: Lat 2 of the RegattaCoursePoint
 *          type: integer
 *        Lng2:
 *          description: Lng 2 of the RegattaCoursePoint
 *          type: integer
 *        TrackerId:
 *          description: Tracker ID of the RegattaEntry
 *          type: integer
 *        TrackerId2:
 *          description: Tracker ID 2 of the RegattaEntry
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaCoursePointCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCoursePointParams'
 *        - type: object
 *          required:
 *            - RegattaCourseId
 *            - SortId
 *            - Type
 *            - Side
 *            - Name
 *            - Color
 *            - Lat
 *            - Lng
 *
 *  @swagger
 *  definitions:
 *    RegattaCoursePointQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaCoursePoint
 *              type: string
 *        - $ref: '#/definitions/RegattaCoursePointParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaCoursePointUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCoursePointQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaCoursePoint:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCoursePointCreateParams'
 *        - $ref: '#/definitions/RegattaCoursePointUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaCoursePoint:
 *        allOf:
 *          - $ref: '#/definitions/RegattaCoursePoint'
 *              RegattaCourse:
 *                description: RegattaCourse of the RegattaCoursePoint
 *                $ref: '#/components/schemas/RegattaCourse'
 */

// Enums
import {
  RegattaCoursePointSide,
  RegattaCoursePointType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaCourseId: string;
};

export type $Params = {
  Color: string;
  Color2: null | string;
  Lat: number;
  Lat2: null | number;
  Lng: number;
  Lng2: null | number;
  Name: string;
  Name2: null | string;
  Side: RegattaCoursePointSide;
  SortId: number;
  TrackerId: null | number;
  TrackerId2: null | number;
  Type: RegattaCoursePointType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaCourse?: $Model<'RegattaCourse'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Color: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Color2: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Lat: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Lat2: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Lng: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Lng2: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name2: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Side: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaCoursePointSide)),
    validate: {
      notEmpty: true,
    },
  },
  SortId: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  TrackerId: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  TrackerId2: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaCoursePointType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaCoursePoint'> => {
  const RegattaCoursePoint = stack.define(
    'RegattaCoursePoint',
    fields(Sequelize),
    {
      tableName: 'RegattaCoursePoints',
    },
  );

  RegattaCoursePoint.associate = (models: $ModelClasses) => {
    models.RegattaCoursePoint.belongsTo(
      models.RegattaCourse,
      {
        as: 'RegattaCourse',
        foreignKey: 'RegattaCourseId',
      },
    );
  };

  RegattaCoursePoint.addScope(
    'defaultScope',
    {
      order: [['SortId', 'ASC']],
    },
    {
      override: true,
    },
  );

  return RegattaCoursePoint;
});
